import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RegistrationProgressIndicator from '../../components/ui/RegistrationProgressIndicator';
import PlayerDocumentCard from './components/PlayerDocumentCard';
import DocumentUploadZone from './components/DocumentUploadZone';
import BulkUploadModal from './components/BulkUploadModal';
import DocumentCompletionSummary from './components/DocumentCompletionSummary';
import { registrationService } from '../../services/registrationService';
import { supabase } from '../../lib/supabase';

const DocumentUploadCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [currentRegistrationId, setCurrentRegistrationId] = useState(null);

  const [documents, setDocuments] = useState({});
  const [uploadingDocuments, setUploadingDocuments] = useState({});

  useEffect(() => {
    const loadPlayersAndDocuments = async () => {
      try {
        setLoading(true);
        const mobileNumber = localStorage.getItem('userMobile') || '';
        
        // Get registration ID from navigation state or from draft
        let registrationId = location?.state?.registrationId;
        console.log('Document Upload Center - Received registrationId from navigation state:', registrationId);
        
        if (!registrationId) {
          console.warn('No registrationId in navigation state, fetching draft by mobile number');
          // Get the current draft registration
          const draft = await registrationService.getDraftByMobile(mobileNumber);
          if (!draft) {
            navigate('/team-registration-form', { state: { registrationId: null } });
            return;
          }
          registrationId = draft.id;
        }

        setCurrentRegistrationId(registrationId);

        // Get players for this registration
        const playersData = await registrationService.getPlayersByRegistration(registrationId);
        
        // Transform players to match component expectations
        const transformedPlayers = playersData?.map(player => ({
          id: player.id,
          name: `${player.first_name} ${player.middle_name ? player.middle_name + ' ' : ''}${player.last_name}`.trim(),
          role: player.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Player',
          mcaId: player.mca_id || 'N/A',
          photo: null,
          photoAlt: `Player ${player.first_name} ${player.last_name}`
        })) || [];

        setPlayers(transformedPlayers);
        
        // Load documents for all players in parallel
        const documentsMapPromises = transformedPlayers.map(async (player) => {
          try {
            const playerDocs = await registrationService.getPlayerDocuments(player.id);
            // Map database documents to UI format
            const uiDocs = {};
            
            // Generate signed URLs for all documents in parallel
            const docPromises = playerDocs.map(async (doc) => {
              // Map database enum back to UI format
              let uiType = doc.document_type;
              if (doc.document_type === 'aadhar_card') uiType = 'aadhar';
              else if (doc.document_type === 'birth_certificate') uiType = 'birthCertificate';
              else if (doc.document_type === 'passport_photo') uiType = 'passportPhoto';
              else if (doc.document_type === 'school_id_card') uiType = 'schoolId';
              else if (doc.document_type === 'bonafide_certificate') uiType = 'bonafide';
              
              // Get signed URL from storage (works for private buckets)
              // file_path format: "player-documents/playerId/documentType_timestamp.ext"
              const fileName = doc.file_path.replace('player-documents/', '');
              const { data: urlData, error: urlError } = await supabase?.storage
                ?.from('player-documents')
                ?.createSignedUrl(fileName, 3600); // 1 hour expiration
              
              if (urlError) {
                console.error(`Error creating signed URL for ${doc.file_path}:`, urlError);
              }
              
              return {
                uiType,
                docData: {
                  name: fileName.split('/').pop(),
                  size: doc.file_size,
                  type: 'application/pdf', // Default, could be improved
                  uploadedAt: doc.uploaded_at,
                  url: urlData?.signedUrl || null,
                  filePath: doc.file_path,
                  dbId: doc.id
                }
              };
            });
            
            // Wait for all signed URLs for this player's documents
            const processedDocs = await Promise.all(docPromises);
            processedDocs.forEach(({ uiType, docData }) => {
              uiDocs[uiType] = docData;
            });
            
            return { playerId: player.id, uiDocs };
          } catch (error) {
            console.error(`Error loading documents for player ${player.id}:`, error);
            return { playerId: player.id, uiDocs: {} };
          }
        });
        
        // Wait for all players' documents to load in parallel
        const documentsMapResults = await Promise.all(documentsMapPromises);
        const documentsMap = {};
        documentsMapResults.forEach(({ playerId, uiDocs }) => {
          documentsMap[playerId] = uiDocs;
        });
        
        setDocuments(documentsMap);
        
        if (transformedPlayers.length > 0 && !selectedPlayerId) {
          setSelectedPlayerId(transformedPlayers[0].id);
        }
      } catch (error) {
        console.error('Error loading players:', error);
        navigate('/team-registration-form', { state: { registrationId: currentRegistrationId } });
      } finally {
        setLoading(false);
      }
    };

    loadPlayersAndDocuments();
  }, [navigate, location]);

  const handleFileSelect = async (documentType, fileData) => {
    if (!selectedPlayerId || !fileData) return;

    // Check if we have the actual File object
    if (!fileData.file) {
      console.error('File object not provided');
      return;
    }

    try {
      setUploadingDocuments(prev => ({
        ...prev,
        [`${selectedPlayerId}_${documentType}`]: true
      }));

      // Upload file to Supabase storage
      const uploadResult = await registrationService.uploadPlayerDocument(
        selectedPlayerId,
        fileData.file, // The actual File object
        documentType
      );

      // Save document record to database
      await registrationService.savePlayerDocument(
        selectedPlayerId,
        documentType,
        uploadResult.filePath,
        fileData.size
      );

      // Update local state with document info
      setDocuments((prev) => ({
        ...prev,
        [selectedPlayerId]: {
          ...prev?.[selectedPlayerId],
          [documentType]: {
            name: fileData.name,
            size: fileData.size,
            type: fileData.type,
            uploadedAt: new Date().toISOString(),
            url: uploadResult.fileUrl,
            filePath: uploadResult.filePath
          }
        }
      }));
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document: ' + error?.message);
    } finally {
      setUploadingDocuments(prev => {
        const newState = { ...prev };
        delete newState[`${selectedPlayerId}_${documentType}`];
        return newState;
      });
    }
  };

  const handleFileRemove = async (documentType) => {
    if (!selectedPlayerId) return;

    try {
      // Delete from Supabase
      await registrationService.deletePlayerDocument(selectedPlayerId, documentType);

      // Update local state
      setDocuments((prev) => {
        const playerDocs = { ...prev?.[selectedPlayerId] };
        delete playerDocs?.[documentType];
        return {
          ...prev,
          [selectedPlayerId]: playerDocs
        };
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document: ' + error?.message);
    }
  };

  const handleBulkUpload = async (playerId, files) => {
    const documentTypes = ['aadhar', 'birthCertificate', 'passportPhoto', 'schoolId', 'bonafide'];
    
    try {
      for (let index = 0; index < Math.min(files.length, documentTypes.length); index++) {
        const file = files[index];
        const documentType = documentTypes[index];
        
        try {
          setUploadingDocuments(prev => ({
            ...prev,
            [`${playerId}_${documentType}`]: true
          }));

          // Upload file to Supabase storage
          const uploadResult = await registrationService.uploadPlayerDocument(
            playerId,
            file,
            documentType
          );

          // Save document record to database
          await registrationService.savePlayerDocument(
            playerId,
            documentType,
            uploadResult.filePath,
            file.size
          );

          // Update local state
          setDocuments((prev) => ({
            ...prev,
            [playerId]: {
              ...prev?.[playerId],
              [documentType]: {
                name: file.name,
                size: file.size,
                type: file.type,
                uploadedAt: new Date().toISOString(),
                url: uploadResult.fileUrl,
                filePath: uploadResult.filePath
              }
            }
          }));
        } catch (error) {
          console.error(`Error uploading ${documentType}:`, error);
          alert(`Failed to upload ${documentType}: ${error?.message}`);
        } finally {
          setUploadingDocuments(prev => {
            const newState = { ...prev };
            delete newState[`${playerId}_${documentType}`];
            return newState;
          });
        }
      }
    } catch (error) {
      console.error('Error in bulk upload:', error);
    }
  };

  const calculateCompletionPercentage = (playerId) => {
    const playerDocs = documents?.[playerId] || {};
    const uploadedCount = Object.values(playerDocs)?.filter((doc) => doc !== null)?.length;
    return Math.round(uploadedCount / 5 * 100);
  };

  const selectedPlayer = players?.find((p) => p?.id === selectedPlayerId);
  const selectedPlayerDocs = documents?.[selectedPlayerId] || {};

  const allDocumentsUploaded = players?.every((player) => {
    const playerDocs = documents?.[player?.id] || {};
    return Object.keys(playerDocs)?.length === 5 && Object.values(playerDocs)?.every((doc) => doc !== null);
  });

  const handleProceed = () => {
    if (!allDocumentsUploaded) {
      alert('Please upload all required documents for all players before proceeding.');
      return;
    }
    if (!currentRegistrationId) {
      alert('Registration ID is missing. Please try again.');
      return;
    }
    navigate('/declaration-and-submission', { state: { registrationId: currentRegistrationId } });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-4 lg:p-6 space-y-6">
        <RegistrationProgressIndicator 
          customSteps={[
            { id: 1, label: 'Team Registration', completed: true, active: false },
            { id: 2, label: 'Player Registration', completed: true, active: false },
            { id: 3, label: 'Document Upload', completed: false, active: true },
            { id: 4, label: 'Declaration & Submission', completed: false, active: false }
          ]} 
        />

        <div className="bg-card rounded-lg border border-border p-4 lg:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground flex items-center gap-2 sm:gap-3">
                <Icon name="Upload" size={24} className="text-primary flex-shrink-0" />
                <span>Document Upload Center</span>
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                Upload required documents for each registered player
              </p>
            </div>
            <Button
              variant="outline"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/team-registration-form', { state: { registrationId: currentRegistrationId } })}
              className="w-full sm:w-auto touch-target"
              size="sm">
              Back
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-foreground">Players</h2>
                <button
                  onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
                  className="lg:hidden p-2 hover:bg-muted rounded-md">

                  <Icon name={isMobileSidebarOpen ? 'X' : 'Menu'} size={20} />
                </button>
              </div>

              <div
                className={`space-y-3 ${
                isMobileSidebarOpen ? 'block' : 'hidden lg:block'}`
                }>

                {loading ? (
                  <div className="text-muted-foreground text-sm">Loading players...</div>
                ) : players?.length > 0 ? (
                  players?.map((player) =>
                    <PlayerDocumentCard
                      key={player?.id}
                      player={player}
                      isSelected={selectedPlayerId === player?.id}
                      onSelect={() => {
                        setSelectedPlayerId(player?.id);
                        setIsMobileSidebarOpen(false);
                      }}
                      completionPercentage={calculateCompletionPercentage(player?.id)} />
                  )
                ) : (
                  <div className="text-muted-foreground text-sm">No players found. Please register players first.</div>
                )}

                {/* Bulk Upload button hidden */}
                {/* <Button
                  variant="outline"
                  fullWidth
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => setIsBulkUploadOpen(true)}
                  className="mt-4">

                  Bulk Upload
                </Button> */}
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              <DocumentCompletionSummary
                players={players}
                documents={documents} />


              {selectedPlayer &&
              <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">
                        {selectedPlayer?.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedPlayer?.role} • {selectedPlayer?.mcaId}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DocumentUploadZone
                    documentType="aadhar"
                    uploadedFile={selectedPlayerDocs?.aadhar}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove} />

                    <DocumentUploadZone
                    documentType="birthCertificate"
                    uploadedFile={selectedPlayerDocs?.birthCertificate}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove} />

                    <DocumentUploadZone
                    documentType="passportPhoto"
                    uploadedFile={selectedPlayerDocs?.passportPhoto}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove}
                    acceptedFormats=".jpg,.jpeg,.png" />

                    <DocumentUploadZone
                    documentType="schoolId"
                    uploadedFile={selectedPlayerDocs?.schoolId}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove} />

                    <DocumentUploadZone
                    documentType="bonafide"
                    uploadedFile={selectedPlayerDocs?.bonafide}
                    onFileSelect={handleFileSelect}
                    onFileRemove={handleFileRemove} />

                  </div>
                </div>
              }

              <div className="flex items-center justify-between pt-6 border-t border-border">
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={() => navigate('/team-registration-form', { state: { registrationId: currentRegistrationId } })}>

                  Previous Step
                </Button>
                <Button
                  variant="default"
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={handleProceed}
                  disabled={!allDocumentsUploaded}>

                  Proceed to Declaration
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BulkUploadModal
        isOpen={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        players={players}
        onBulkUpload={handleBulkUpload} />

    </div>);

};

export default DocumentUploadCenter;